import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { Comment, CommentOutlined } from "@mui/icons-material";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { getOfflineData } from "../../../utils/offline-services";
import { calculateItemAmounts, PR_ITEM_MODEL } from "./helper";
import RemarksDialog from "./remarks-dialog";
import { applySingleStoreUploadToRows, getSSRowKey } from "./single-store-upload";

// Duration of the post-upload reorder slide (FLIP), in ms.
const REORDER_ANIMATION_MS = 450;

// Disabled inputs don't fire mouse events, so the tooltip hangs off a wrapper span.
const FullNameTooltipField = ({ label, value }) => (
  <Tooltip title={value || ""} placement="top-start" enterDelay={300} disableInteractive>
    <span className="pr-item-tooltip-cell">
      <TextField size="small" label={label} value={value || ""} disabled fullWidth />
    </span>
  </Tooltip>
);

// Concurrency cap for per-item vendor-mapping lookups during bulk auto-population,
// so a large subcategory item list doesn't fire hundreds of simultaneous requests.
const VENDOR_MAPPING_LOOKUP_CONCURRENCY = 8;

// Same endpoint/status-filter/max-price logic as the HO flow's PRItemRow.handleItemChange.
const fetchApprovedVendorMappings = (itemId, prDate) =>
  invokeApi(
    HTTP_METHODS.GET,
    `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.VENDOR_ITEM_MAPPING}`,
    null,
    {
      item: itemId,
      status: "approved",
      limit: 1000,
      ...(prDate ? { date: prDate } : {}),
    }
  )
    .then((res) => {
      const mappings = Array.isArray(res) ? res : res?.results || [];
      return mappings.filter((m) => m.status === "approved");
    })
    .catch(() => []);

// Compares each row's already-set item-master price against the max approved
// vendor-mapping price (if any exist) and floors the rate at whichever is higher -- a
// Vendor Item Mapping must never blindly override the Item Master price. The winning
// mapping's own tax rate travels with its price (never mixed with the other source's tax).
// Lookups run in parallel chunks to cap in-flight requests during bulk auto-population.
const applyVendorMappingOverrides = async (rows, prDate) => {
  const result = [...rows];
  for (let i = 0; i < result.length; i += VENDOR_MAPPING_LOOKUP_CONCURRENCY) {
    const chunk = result.slice(i, i + VENDOR_MAPPING_LOOKUP_CONCURRENCY);
    const approvedLists = await Promise.all(
      chunk.map((row) => fetchApprovedVendorMappings(row.item, prDate))
    );
    approvedLists.forEach((approved, idx) => {
      if (approved.length === 0) return;
      const bestVendorMapping = approved.reduce(
        (best, m) => ((parseFloat(m.price) || 0) > (parseFloat(best.price) || 0) ? m : best),
        approved[0]
      );
      const vendorPrice = parseFloat(bestVendorMapping.price) || 0;
      const vendorTax = bestVendorMapping.taxPercentage != null ? parseFloat(bestVendorMapping.taxPercentage) || 0 : 0;
      const itemMasterPrice = parseFloat(result[i + idx].estimatedRate) || 0;
      const itemMasterTax = parseFloat(result[i + idx].taxRate) || 0;
      const vendorWins = vendorPrice >= itemMasterPrice;
      const resolvedRate = vendorWins ? vendorPrice : itemMasterPrice;
      const resolvedTax = vendorWins ? vendorTax : itemMasterTax;
      result[i + idx] = {
        ...result[i + idx],
        estimatedRate: String(resolvedRate),
        taxRate: String(resolvedTax),
        rateContract: true,
        vendorMappings: approved,
      };
    });
  }
  return result;
};

// Individual read-only row with qty as the only editable field
const SingleStorePRRow = ({ prItem, budgetHeadName, subcategoryList = [], onUpdate }) => {
  const [remarksOpen, setRemarksOpen] = useState(false);
  const canEditRemarks = Number(prItem.quantity) > 0;

  const handleQtyChange = (e) => {
    // A single-store item only ever has one store, so an edited flat quantity is always
    // authoritative -- clear any (possibly server-derived/stale) allocations so
    // getPooledQuantity/calculateItemAmounts use the just-typed value instead of freezing
    // on the old allocations-derived sum, and so buildEditPayload sends the new quantity
    // on save instead of a stale allocations array. Mirrors how the HO multi-store row
    // (handlePooledQuantityChange in add-item-modal.js) rebuilds allocations from the new
    // value rather than leaving them stale.
    const updated = { ...prItem, quantity: e.target.value, allocations: [] };
    onUpdate({ ...updated, ...calculateItemAmounts(updated) });
  };

  // auto-populate-items returns subCategory as a raw id (not a populated object), so the
  // display name has to be resolved against the line item's subcategory list.
  const subCategoryName =
    subcategoryList.find(
      (sc) => String(sc._id || sc.id) === String(prItem.subCategory)
    )?.name || "";

  return (
    <div className="pr-item-row pr-ss-row">
      <FullNameTooltipField label="Subcategory" value={subCategoryName} />

      <FullNameTooltipField label="Item" value={prItem.itemName} />

      <TextField size="small" label="Description" value={prItem.description || ""} disabled />

      <TextField size="small" label="HSN" value={prItem.hsnCode || ""} disabled />

      <TextField size="small" label="UOM" value={prItem.uom || ""} disabled />

      <TextField
        size="small"
        label="Qty *"
        type="number"
        value={prItem.quantity ?? 0}
        onChange={handleQtyChange}
        inputProps={{ min: 0 }}
      />

      <TextField size="small" label="Rate (₹)" value={prItem.estimatedRate || ""} disabled />

      <TextField size="small" label="Tax %" value={prItem.taxRate || ""} disabled />

      <TextField
        size="small"
        label="Amount"
        disabled
        value={prItem.estimatedAmount ? `₹${Number(prItem.estimatedAmount).toFixed(2)}` : ""}
      />

      <TextField
        size="small"
        label="Tax Amt"
        disabled
        value={prItem.estimatedTaxAmount ? `₹${Number(prItem.estimatedTaxAmount).toFixed(2)}` : ""}
      />

      <TextField
        size="small"
        label="Total"
        disabled
        value={prItem.estimatedTotalAmount ? `₹${Number(prItem.estimatedTotalAmount).toFixed(2)}` : ""}
      />

      <TextField size="small" label="Budget Head" value={budgetHeadName || ""} disabled />

      <div className="pr-item-remarks-cell">
        <IconButton
          size="small"
          onClick={() => setRemarksOpen(true)}
          title={canEditRemarks ? (prItem.remarks ? "View/edit remarks" : "Add remarks") : "View remarks"}
        >
          {prItem.remarks ? (
            <Comment fontSize="small" color="primary" />
          ) : (
            <CommentOutlined fontSize="small" />
          )}
        </IconButton>
      </div>

      <RemarksDialog
        open={remarksOpen}
        initialValue={prItem.remarks}
        readOnly={!canEditRemarks}
        onClose={() => setRemarksOpen(false)}
        onSave={(value) => {
          onUpdate({ ...prItem, remarks: value });
          setRemarksOpen(false);
        }}
      />
    </div>
  );
};

const ColumnHeader = () => (
  <div className="pr-item-row pr-ss-row pr-item-row-header">
    <span>Subcategory</span>
    <span>Item</span>
    <span>Description</span>
    <span>HSN</span>
    <span>UOM</span>
    <span>Qty</span>
    <span>Rate (₹)</span>
    <span>Tax %</span>
    <span>Amount</span>
    <span>Tax Amt</span>
    <span>Total</span>
    <span>Budget Head</span>
    <span>Remarks</span>
  </div>
);

// Section component: owns the item fetch for one budget line item.
// Always fetches the full category/subcategory catalog (same as Create PR) and, when
// existingItems (edit mode) is non-empty, merges saved quantity/rate/tax/remarks/vendor
// mapping/allocations/_id onto the matching catalog rows by item id -- so Edit PR shows the
// full addable catalog instead of only the items already on the PR. existingItems is always
// [] for Create PR, so the merge is a no-op there.
// For store users the fetched list is filtered client-side to items where displayToStore === true.
// Backend receives only subCategory, page, and limit.
// `bulkUpload` ({ nonce, updates }, Create PR only) carries an Excel upload's per-row
// quantities; each new nonce is applied once, and rows with Qty > 0 then slide to the top.
const SingleStorePRSection = ({
  lineItem,
  existingItems,
  subcategoryList = [],
  onItemsChange,
  prDate,
  bulkUpload
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const rowElsRef = useRef(new Map());
  const lastUploadNonceRef = useRef(null);
  // Row tops captured just before an upload-driven reorder (FLIP "first" positions).
  const pendingFlipRef = useRef(null);

  const safeOnItemsChange = onItemsChange || (() => {});
  useEffect(() => {
    setLoading(true);

    const userDetails = getOfflineData("user") || {};
    const isStoreUser = userDetails?.locationType !== "HO";

    const catId =
      lineItem.category?._id ||
      lineItem.category?.id ||
      lineItem.category;

    // Saved items for this line item (Edit PR), keyed by item-master id so the freshly
    // fetched catalog rows below can be matched against them. Always empty for Create PR.
    const existingByItemId = new Map(
      (existingItems || [])
        .filter((it) => it.item != null)
        .map((it) => [String(it.item), it])
    );

    if (!catId) {
      // No category to fetch a catalog against -- fall back to whatever was already saved
      // instead of wiping the section to empty.
      const fallback = existingItems || [];
      setItems(fallback);
      safeOnItemsChange(fallback);
      setLoading(false);
      return;
    }

    const params = {
      category: catId,
      lineItemId: lineItem.id
    };

    if (lineItem.subCategory != null && lineItem.subCategory !== '') {
      params.subCategory = Number(lineItem.subCategory);
    }

    // auto-populate-items is the backend's own categoryMappings-resolution endpoint: for an
    // item with more than one categoryMappings pair, it returns one row per pair that
    // actually matches this line item's category (each carrying that pair's own
    // categoryMappingId/category/subCategory), instead of this component re-deriving a
    // (possibly wrong) pairing from the item's deprecated single category/subCategory
    // mirror. It also already carries the legacy-item fallback for items not yet migrated
    // onto categoryMappings, so single-mapping items keep working unchanged.
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_AUTO_POPULATE_ITEMS}`,
      null,
      params
    )
      .then(async (res) => {
        if (!res?.message) {
          const all = Array.isArray(res?.items) ? res.items : [];

          const rows = all.map((row) => ({
            ...PR_ITEM_MODEL,
            item: row.item,
            itemName: row.itemName || "",
            hsnCode: row.hsnCode || "",
            uom: row.uom || "",
            taxRate: row.taxRate != null ? String(row.taxRate) : "",
            estimatedRate: row.estimatedRate != null ? String(row.estimatedRate) : "",
            description: row.description || "",
            quantity: 0,
            budgetHead: lineItem.name || "",
            category: row.category,
            subCategory: row.subCategory,
            categoryMappingId: row.categoryMappingId,
            _tempId: `ss-${lineItem.id}-${row.item}`
          }));

          // Split into rows that already have saved data (merged so the saved
          // quantity/rate/tax/remarks/vendor/allocation/_id survive -- that data was locked
          // in at a previous save and must not be re-derived) and rows that don't (only
          // these get a vendor-mapping lookup + start at quantity 0, same as Create PR).
          const matchedItemIds = new Set();
          const mergedRows = [];
          const rowsNeedingOverride = [];

          rows.forEach((catalogRow) => {
            const existingMatch = existingByItemId.get(String(catalogRow.item));
            if (existingMatch) {
              matchedItemIds.add(String(catalogRow.item));
              mergedRows.push({
                ...catalogRow,
                ...existingMatch,
                // Backend never stores an item-name snapshot on a saved PR item -- take it
                // from the live catalog.
                itemName: existingMatch.itemName || catalogRow.itemName,
              });
            } else {
              mergedRows.push(catalogRow);
              rowsNeedingOverride.push(catalogRow);
            }
          });

          // Saved items whose item id no longer appears in the freshly fetched catalog
          // (deactivated/recategorized since the PR was created) -- preserved as-is so
          // nothing already on the PR is silently dropped.
          const leftoverExisting = (existingItems || []).filter(
            (it) => it.item == null || !matchedItemIds.has(String(it.item))
          );

          const overriddenRows = await applyVendorMappingOverrides(rowsNeedingOverride, prDate);
          const overrideByTempId = new Map(overriddenRows.map((r) => [r._tempId, r]));
          const finalRows = mergedRows.map((row) =>
            overrideByTempId.has(row._tempId) ? overrideByTempId.get(row._tempId) : row
          );

          const combined = [...finalRows, ...leftoverExisting];
          setItems(combined);
          safeOnItemsChange(combined);
          setLoading(false);
        } else {
          const fallback = existingItems || [];
          if (fallback.length > 0) {
            setItems(fallback);
            safeOnItemsChange(fallback);
          }
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        const fallback = existingItems || [];
        if (fallback.length > 0) {
          setItems(fallback);
          safeOnItemsChange(fallback);
        }
        setLoading(false);
      });
  }, [lineItem.id, lineItem.category, lineItem.subCategory]);

  const handleUpdate = (index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = updatedItem;
    setItems(newItems);
    onItemsChange(newItems);
  };

  useEffect(() => {
    if (loading || !bulkUpload?.nonce || bulkUpload.nonce === lastUploadNonceRef.current) return;
    lastUploadNonceRef.current = bulkUpload.nonce;

    const applied = applySingleStoreUploadToRows(items, bulkUpload.updates);
    const isIncluded = (row) => (parseFloat(row.quantity) || 0) > 0;
    // Stable partition: included rows first, each group keeping its existing order.
    const reordered = [...applied.filter(isIncluded), ...applied.filter((row) => !isIncluded(row))];

    const firstTops = new Map();
    rowElsRef.current.forEach((el, key) => firstTops.set(key, el.getBoundingClientRect().top));
    pendingFlipRef.current = firstTops;

    setItems(reordered);
    safeOnItemsChange(reordered);
  }, [bulkUpload?.nonce, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // FLIP: after the reordered rows render, offset each back to where it was, then let a
  // CSS transform transition carry it to its new slot.
  useLayoutEffect(() => {
    const firstTops = pendingFlipRef.current;
    if (!firstTops) return;
    pendingFlipRef.current = null;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const moved = [];
    rowElsRef.current.forEach((el, key) => {
      const firstTop = firstTops.get(key);
      if (firstTop == null) return;
      const delta = firstTop - el.getBoundingClientRect().top;
      if (!delta) return;
      el.style.transition = "none";
      el.style.transform = `translateY(${delta}px)`;
      moved.push(el);
    });
    if (moved.length === 0) return;

    // Force the offset to be painted before releasing it.
    moved[0].getBoundingClientRect();
    requestAnimationFrame(() => {
      moved.forEach((el) => {
        el.style.transition = `transform ${REORDER_ANIMATION_MS}ms cubic-bezier(0.2, 0, 0, 1)`;
        el.style.transform = "";
      });
    });
    const cleanup = setTimeout(() => {
      moved.forEach((el) => {
        el.style.transition = "";
      });
    }, REORDER_ANIMATION_MS + 50);
    return () => clearTimeout(cleanup);
  }, [items]);

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Loading items…
      </Typography>
    );
  }

  return (
    <>
      <ColumnHeader />
      {items.map((prItem, index) => {
        const rowKey = getSSRowKey(prItem) ?? `idx-${index}`;
        return (
          <div
            key={rowKey}
            className="pr-ss-row-slot"
            ref={(el) => {
              if (el) rowElsRef.current.set(rowKey, el);
              else rowElsRef.current.delete(rowKey);
            }}
          >
            <SingleStorePRRow
              prItem={prItem}
              budgetHeadName={lineItem.name || ""}
              subcategoryList={subcategoryList}
              onUpdate={(item) => handleUpdate(index, item)}
            />
          </div>
        );
      })}
    </>
  );
};

export { SingleStorePRRow };
export default SingleStorePRSection;
